import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HardHat, Mail, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.toLowerCase().trim() });
      setSent(true);
    } catch { setSent(true); /* always silent — don't leak */ }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-screen flex items-center justify-center p-6 bg-[#F4F4F5]">
      <div className="w-full max-w-md bg-white border-4 border-[#09090B]">
        <div className="bg-[#09090B] p-4 flex items-center gap-2 text-white">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#FF5F15] flex items-center justify-center">
              <HardHat className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black">FIELDLINK<span className="text-[#FF5F15]">.CO.NZ</span></span>
          </Link>
        </div>
        <div className="p-8">
          {sent ? (
            <div data-testid="forgot-sent">
              <div className="w-12 h-12 bg-green-100 border-2 border-green-500 flex items-center justify-center mb-4">
                <Check className="w-6 h-6 text-green-700" strokeWidth={3} />
              </div>
              <h1 className="font-display text-2xl font-black uppercase tracking-tight">Check your inbox</h1>
              <p className="text-sm text-zinc-600 mt-3">
                If <b>{email}</b> matches an account, we&rsquo;ve sent a reset link. The link expires in 60 minutes.
              </p>
              <p className="text-xs text-zinc-500 mt-3">No email? Check spam, or contact your office admin.</p>
              <Link to="/login" className="block mt-6">
                <Button className="w-full h-11 rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white uppercase font-bold tracking-wider">Back to sign in</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="label-tag">/ password reset</div>
              <h1 className="font-display text-2xl font-black uppercase tracking-tight mt-2">Forgot your password?</h1>
              <p className="text-sm text-zinc-600 mt-2">Enter your email and we&rsquo;ll send you a link to set a new one.</p>
              <div className="mt-6">
                <Label htmlFor="email" className="label-tag">Work email</Label>
                <div className="relative mt-2">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input
                    id="email"
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 pl-9 rounded-sm border-zinc-400"
                    placeholder="you@example.co.nz"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full h-12 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase font-bold tracking-wider mt-6"
                data-testid="forgot-submit-button"
              >
                {busy ? "Sending…" : <>Send reset link <ArrowRight className="ml-2 w-4 h-4" /></>}
              </Button>
              <div className="mt-6 text-center text-sm">
                <Link to="/login" className="text-zinc-600 hover:text-[#FF5F15] hover:underline">Back to sign in</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
